import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import { SelectProfitMarginBudgetConfig } from "../../shared/components/formulario/input-select-profit-margin-budget-config/selectProfitMarginBudgetConfig";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { StepsEditor } from "./components/StepsEditor";
import { BudgetTotalFooterContainer } from "./components/BudgetTotalFooterContainer";
import InputData from "../../shared/components/formulario/input-data/inputData";
import { DiscountTypeEnum, PersonTypeEnum } from "../../shared/utils/enum/enum";
import { ICnpjData } from "../../shared/components/buscarCNPJ/buscar-cnpj";
import { Box, Divider, Typography, useMediaQuery, useTheme, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { EditarOrcamentoFormData, editarOrcamentoSchema } from "./schema/editar-orcamento.schema";
import { StutusBudgetEnum, EditarOrcamentoContract } from "./contracts/editar-orcamento.contract";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import { PessoaJuridicaFormPart } from "../../shared/components/formulario/parts/PessoaJuridicaFormPart/pessoa-juridica.part";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import { useEffect, useRef, useState } from "react";
import { orcamentoService } from "./service/orcamento.service";
import { formatDate } from "../../shared/utils";
import { UnitOfMeasureEnum } from "../../shared/components/formulario/select/SelectUnitOfMeasure";
import { CostTypeEnum } from "../empresa/contracts/labor-budget";
import { toastError } from "../../shared/services/errors/erros-handler";
import { useLoader } from "../../shared/contexts/LoaderContext";
import Input from "../../shared/components/formulario/input/input";
import { InputSwitch } from "../../shared/components/formulario/input-switch/switch";
import { mascaraTelefone } from "../../shared/utils/mascaras";
import { AprovarOrcamentoModal } from "./components/ApproveBudgetModal";
import { AiAnaliseOrcamentoModal } from "./components/AiAnaliseOrcamentoModal";
import { FloatingSaveButton, SelectField, TextField } from "../../shared/components/ui";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import { InputRadio } from "../../shared/components/formulario/input-radio/InputRadio";
import { findFirstErrorMessage } from "../../shared/utils/form/find-first-error-message";
import { limpaCamposPsFisica, limpaCamposPsJuridica } from "../../shared/utils/limpa-campo-form/limpa-campos.part"; 

export default function EditarOrcamento() {

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { showLoader, hideLoader } = useLoader();
    const methods = useForm<EditarOrcamentoFormData>({
        resolver: yupResolver(editarOrcamentoSchema),
    });

    function cleanPayload(data: EditarOrcamentoFormData) {
        return {
            description: data.description,
            status: data.status,
            isClient: data.isClient,
            idClient: data.idClient ?? undefined,
            isProfitMargin: data.isProfitMargin,
            idProfitMargin: data.idProfitMargin ?? undefined,
            percentageExtraCost: data.percentageExtraCost ?? undefined,
            personType: data.personType ?? undefined,
            fantasyName: data.fantasyName ?? undefined,
            corporateName: data.corporateName ?? undefined,
            cnpj: data.cnpj ?? undefined,
            cpf: data.cpf ?? undefined,
            fullName: data.fullName ?? undefined,
            email: data.email ?? undefined,
            phone: data.phone ?? undefined,
            dateValidation: data.dateValidation,
            isDiscount: data.isDiscount,
            discountType: data.discountType ?? undefined,
            discountValue: data.discountValue ?? undefined,
            observation: data.observation ?? undefined,
            address: data.address,
            steps: data.steps.map(step => ({
                id: step.id,
                description: step.description,
                costType: step.costType,
                mediumTerm: step.mediumTerm ?? undefined,
                itens: step.itens.map(item => ({
                    id: item.id,
                    isLabor: item.isLabor,
                    idLaborBudget: item.isLabor ? item.idLaborBudget : undefined,
                    description: !item.isLabor ? item.description : undefined,
                    quantity: item.quantity,
                    unitOfMeasure: !item.isLabor ? item.unitOfMeasure : undefined,
                    unitValue: !item.isLabor ? item.unitValue : undefined,
                }))
            })),
        };
    }

    useEffect(() => {
        if (!id) return;
        showLoader();
        orcamentoService.detalhar(id)
            .then((orcamento) => {
                methods.reset({
                    ...orcamento,
                    personType: orcamento.cnpj ? PersonTypeEnum.PJ : PersonTypeEnum.PF,
                    isProfitMargin: orcamento.idProfitMargin ? true : false,
                    idProfitMargin: orcamento.idProfitMargin ?? undefined,
                    steps: orcamento.steps ?? [],
                    percentageExtraCost: orcamento.percentageExtraCost ?? 0,
                    isDiscount: orcamento.isDiscount ?? false,
                    discountType: orcamento.discountType ?? undefined,
                    discountValue: orcamento.discountValue ?? 0,
                    dateValidation: formatDate(orcamento.dateValidation),
                } as any as EditarOrcamentoFormData);
                setTimeout(() => {
                    methods.trigger();
                    hideLoader();
                }, 1000);
            })
            .catch(() => {
                hideLoader();
                toast.error("Erro ao carregar orçamento.");
            });
    }, [id, methods]);

    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));
    const {
        handleSubmit,
        setValue,
        watch,
        control,
        formState: { errors },
    } = methods;

    const isClient = watch("isClient");
    const personType = watch("personType");
    const isProfitMargin = watch("isProfitMargin");
    const [isDesconto, setIsDesconto] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);
    const [openAiModal, setOpenAiModal] = useState(false);

    toastError(errors);

    const preencherCampos = (dados: ICnpjData) => {
        setValue("fantasyName" as keyof EditarOrcamentoFormData, dados.fantasia || dados.razaoSocial || "");
        setValue("corporateName" as keyof EditarOrcamentoFormData, dados.razaoSocial || "");
    };

    useEffect(() => {
        setValue("isDiscount" as keyof EditarOrcamentoFormData, isDesconto);
        setIsDesconto(watch("isDiscount") || false);
    }, [isDesconto, setValue]);

    const personTypeAnteriorRef = useRef<PersonTypeEnum | undefined>(undefined);
    useEffect(() => {
        if (personTypeAnteriorRef.current && personTypeAnteriorRef.current !== personType) {
            if (personType === PersonTypeEnum.PJ) {
                limpaCamposPsFisica({ methods });
            } else if (personType === PersonTypeEnum.PF) {
                limpaCamposPsJuridica({ methods });
            }
        }
        personTypeAnteriorRef.current = personType;
    }, [personType, methods]);

    let desabilitarCampos = false;
    if (methods.watch("status") === StutusBudgetEnum.APPROVED) {
        desabilitarCampos = true;
    }

    const { isPending, mutate } = useMutation({
        mutationFn: async (data: EditarOrcamentoFormData) => {
            const payload = cleanPayload(data);
            await orcamentoService.editar(id || "", payload);
        },
        onSuccess: () => {
            hideLoader();
            toast.success("Orçamento editado com sucesso!");
            navigate("/orcamentos", { state: { reload: true } });
        },
        onError: (err: any) => {
            toastError(err);
        },
    });

    // Mesma edição do orçamento, mas permanece na página em vez de navegar
    const { mutate: mutateAndStay, isPending: salvandoFlutuante } = useMutation({
        mutationFn: async (data: EditarOrcamentoFormData) => {
            const payload = cleanPayload(data);
            await orcamentoService.editar(id || "", payload);
        },
        onSuccess: () => {
            hideLoader();
            toast.success("Orçamento salvo com sucesso!");
        },
        onError: (err: any) => {
            toastError(err);
        },
    });

    const onInvalidSubmit = (formErrors: typeof errors) => {
        const primeiraMensagem = findFirstErrorMessage(formErrors);
        toast.error(primeiraMensagem || "Existem campos inválidos no formulário.");
    };

    return (
        <BaseLayout title="Editar Orçamento">
            <FerramanetasDetalhe
                mostraBotaoApagar={false}
                mostrarBotaoAdicional={!desabilitarCampos}
                textoBotaoAdicional="Aprovar Orçamento"
                mostrarBotaoAdicional2
                textoBotaoAdicional2="Analisar com IA"
                iconBotaoAdicional2="auto_awesome"
                aoClicarBotaoAdicional2={() => setOpenAiModal(true)}
                mostraBotaoNovo={false}
                mostraBotaoSalvarEVoltar={false}
                mostraBotaoVoltar
                mostraBotaoSalvar={!desabilitarCampos}
                aoClicarBotaoSalvar={handleSubmit((data: EditarOrcamentoFormData) => mutate(data))}
                aoClicarBotaoVoltar={() => navigate("/orcamentos")}
                aoClicarBotaoAdicional={() => setOpenApproveModal(true)}
            />
            <AiAnaliseOrcamentoModal open={openAiModal} onClose={() => setOpenAiModal(false)} methods={methods} />
            <Box component="form" onSubmit={handleSubmit((data: EditarOrcamentoFormData) => mutate(data))}>
                <Box display="flex" flexDirection="column" gap={4}>
                    {/* Seção: Dados do Cliente */}
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>Dados do Cliente</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <InputSwitch
                                    name="isClient"
                                    label="O cliente já possui cadastro no sistema?"
                                    methods={methods}
                                    disabled={desabilitarCampos}
                                />
                            </Grid>
                            {isClient && (
                                <Grid item xs={12} md={12}>
                                    <Controller
                                        name="idClient"
                                        control={control}
                                        disabled={desabilitarCampos}
                                        render={({ field }) => (
                                            <SelectCliente
                                                value={field.value ?? ""}
                                                onChange={field.onChange}
                                                disabled={desabilitarCampos}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            {!isClient && (
                                <Grid item xs={12} md={12}>
                                    <InputRadio
                                        name="personType"
                                        label="Tipo de pessoa (Física ou Jurídica)"
                                        methods={methods}
                                        options={[
                                            { value: PersonTypeEnum.PJ, label: "Jurídica" },
                                            { value: PersonTypeEnum.PF, label: "Física" },
                                        ]}
                                        row
                                        disabled={desabilitarCampos}
                                    />
                                    {personType === PersonTypeEnum.PJ && (
                                        <PessoaJuridicaFormPart methods={methods} preencherCampos={preencherCampos} disabled={desabilitarCampos} />
                                    )}
                                    {personType === PersonTypeEnum.PF && (
                                        <PessoaFisicaFormPart methods={methods} mdDown={mdDown} disabled={desabilitarCampos} />
                                    )}
                                </Grid>
                            )}
                            {/* Box de Contato */}
                            <Grid item xs={12}>
                                <Box
                                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40"
                                >
                                    <Typography
                                        variant="subtitle1"
                                        className="!text-slate-700 dark:!text-slate-200"
                                        sx={{ mb: 1, fontWeight: 500 }}
                                    >
                                        Contato
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Input
                                                name="email"
                                                label="E-mail"
                                                methods={methods}
                                                type="email"
                                                disabled={desabilitarCampos}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputMascarado
                                                name="phone"
                                                methods={methods}
                                                label="Telefone"
                                                mask={mascaraTelefone}
                                                disabled={desabilitarCampos}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Seção: Endereço */}
                    <Box>
                        <EnderecoFormPart methods={methods} mdDown={mdDown} disabled={desabilitarCampos} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Seção: Dados do Orçamento */}
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>Dados do Orçamento</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <Input
                                    name="description"
                                    label="Nome do Orçamento"
                                    methods={methods}
                                    disabled={desabilitarCampos}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputSwitch
                                    name="isProfitMargin"
                                    label="Deseja incluir margem de lucro no cálculo deste orçamento?"
                                    methods={methods}
                                    disabled={desabilitarCampos}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Controller
                                    name="status"
                                    control={control}
                                    disabled={desabilitarCampos}
                                    render={({ field }) => (
                                        <SelectField
                                            label="Status do Orçamento"
                                            value={field.value ?? StutusBudgetEnum.GENERATED}
                                            onChange={e => field.onChange(e.target.value as StutusBudgetEnum)}
                                            disabled={desabilitarCampos}
                                        >
                                            <option value={StutusBudgetEnum.GENERATED}>Gerado</option>
                                            <option value={StutusBudgetEnum.REJECTED}>Rejeitado</option>
                                            <option value={StutusBudgetEnum.EXPIRED}>Expirado</option>
                                        </SelectField>
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputData
                                    name="dateValidation"
                                    label="Validade do Orçamento"
                                    methods={methods}
                                    helperText={errors.dateValidation?.message}
                                    disabled={desabilitarCampos}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Input
                                    name="percentageExtraCost"
                                    label="Custo Extra (%)"
                                    methods={methods}
                                    type="number"
                                    disabled={desabilitarCampos}
                                />
                            </Grid>
                            {isProfitMargin && (
                                <Grid item xs={12} md={12}>
                                    <SelectProfitMarginBudgetConfig name="idProfitMargin" methods={methods} disabled={desabilitarCampos} />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                        <Typography variant="h6">Etapas e Itens do Orçamento</Typography>
                        <StepsEditor methods={methods} disabled={desabilitarCampos} />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    {/* Seção: Desconto */}
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>Desconto</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <InputSwitch
                                    name="isDiscount"
                                    label="Deseja incluir desconto no cálculo do valor deste orçamento?"
                                    methods={methods}
                                    disabled={desabilitarCampos}
                                />
                            </Grid>
                            {methods.watch("isDiscount") && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <InputRadio
                                            name="discountType"
                                            label="Forma de aplicação do desconto"
                                            methods={methods}
                                            options={[
                                                { value: "percentage", label: "Percentual sobre o valor (%)" },
                                                { value: "fixed", label: "Valor fixo do desconto (R$)" },
                                            ]}
                                            row
                                            disabled={desabilitarCampos}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Controller
                                            name="discountValue"
                                            control={control}
                                            disabled={desabilitarCampos}
                                            render={({ field }) => (
                                                methods.watch("discountType") === "percentage" ? (
                                                    <TextField
                                                        {...field}
                                                        label="Valor do Desconto (%)"
                                                        type="number"
                                                        min={0}
                                                        step={0.01}
                                                        error={!!errors.discountValue}
                                                        helperText={errors.discountValue?.message}
                                                        disabled={desabilitarCampos}
                                                    />
                                                ) : (
                                                    <InputMascarado
                                                        name="discountValue"
                                                        methods={methods}
                                                        label="Valor do Desconto (R$)"
                                                        mask={mascaraMoedaReal}
                                                        helperText={errors.discountValue?.message}
                                                        disabled={desabilitarCampos}
                                                    />
                                                )
                                            )}
                                        />
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <InputDescricao
                                name="observation"
                                methods={methods}
                                label="Observações"
                                placeholder="Escreva observações, escopo ou condições do orçamento..."
                                disabled={desabilitarCampos}
                            />
                        </Grid>
                    </Grid>
                </Box >
            </Box >
            <AprovarOrcamentoModal
                open={openApproveModal}
                idOrcamento={id || ""}
                onClose={() => setOpenApproveModal(false)}
                onSubmit={() => { }}
            />
            <BudgetTotalFooterContainer methods={methods} />
            {!desabilitarCampos && (
                <FloatingSaveButton
                    onClick={handleSubmit((data: EditarOrcamentoFormData) => mutateAndStay(data), onInvalidSubmit)}
                    loading={salvandoFlutuante}
                    title="Salvar sem sair da página"
                />
            )}
        </BaseLayout >
    );
}
function setLoading(arg0: boolean, arg1: string) {
    throw new Error("Function not implemented.");
}

